import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { UserIcon, KeyIcon, Cog6ToothIcon, PlusIcon, PencilIcon, TrashIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import DataTable from '../components/common/DataTable';
import { useAuth } from '../context/AuthContext';
import { leaveService, twoFactorService } from '../services/hrService';
import api from '../services/api';

export default function Settings() {
  const { user, requires2FASetup, refreshUser } = useAuth();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('profile');
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLeaveType, setEditingLeaveType] = useState(null);

  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    email: ''
  });

  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const [leaveTypeData, setLeaveTypeData] = useState({
    name: '',
    code: '',
    default_days: '',
    description: '',
    is_paid: true
  });

  // 2FA states
  const [twoFAStatus, setTwoFAStatus] = useState(null);
  const [twoFASetupData, setTwoFASetupData] = useState(null);
  const [twoFALoading, setTwoFALoading] = useState(false);
  const [twoFACode, setTwoFACode] = useState('');
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [showDisableModal, setShowDisableModal] = useState(false);

  // Auto-switch to security tab if 2FA is required
  useEffect(() => {
    if (requires2FASetup || location.state?.require2FA) {
      setActiveTab('security');
    }
  }, [requires2FASetup, location.state]);

  useEffect(() => {
    if (user) {
      setProfileData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || ''
      });
    }
    if (activeTab === 'leave-types') {
      fetchLeaveTypes();
    }
    if (activeTab === 'security') {
      fetch2FAStatus();
    }
  }, [user, activeTab]);

  const fetch2FAStatus = async () => {
    try {
      const response = await twoFactorService.getStatus();
      setTwoFAStatus(response.data);
    } catch (error) {
      console.error('Error fetching 2FA status:', error);
    }
  };

  const handleSetup2FA = async () => {
    setTwoFALoading(true);
    try {
      const response = await twoFactorService.setup();
      setTwoFASetupData(response.data);
    } catch (error) {
      console.error('Error setting up 2FA:', error);
      alert('Failed to setup 2FA');
    }
    setTwoFALoading(false);
  };

  const handleVerify2FASetup = async (e) => {
    e.preventDefault();
    setTwoFALoading(true);
    try {
      const response = await twoFactorService.verifySetup(twoFACode);
      setBackupCodes(response.data.backup_codes || []);
      setShowBackupCodes(true);
      setTwoFASetupData(null);
      setTwoFACode('');
      fetch2FAStatus();
      // Refresh user data to update 2FA status
      await refreshUser();
    } catch (error) {
      console.error('Error verifying 2FA:', error);
      alert(error.response?.data?.error || 'Invalid verification code');
    }
    setTwoFALoading(false);
  };

  const handleDisable2FA = async (e) => {
    e.preventDefault();
    setTwoFALoading(true);
    try {
      await twoFactorService.disable(disableCode, disablePassword);
      setShowDisableModal(false);
      setDisableCode('');
      setDisablePassword('');
      fetch2FAStatus();
      alert('Two-factor authentication has been disabled');
    } catch (error) {
      console.error('Error disabling 2FA:', error);
      alert(error.response?.data?.error || 'Failed to disable 2FA');
    }
    setTwoFALoading(false);
  };

  const handleRegenerateCodes = async () => {
    if (!window.confirm('This will invalidate your existing backup codes. Continue?')) {
      return;
    }
    setTwoFALoading(true);
    try {
      const response = await twoFactorService.regenerateCodes();
      setBackupCodes(response.data.backup_codes || []);
      setShowBackupCodes(true);
    } catch (error) {
      console.error('Error regenerating codes:', error);
      alert('Failed to regenerate backup codes');
    }
    setTwoFALoading(false);
  };

  const fetchLeaveTypes = async () => {
    try {
      const response = await leaveService.getTypes();
      setLeaveTypes(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching leave types:', error);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/api/users/${user.id}/`, profileData);
      alert('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile');
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (passwordData.new_password !== passwordData.confirm_password) {
      alert('New passwords do not match!');
      return;
    }

    try {
      await api.post('/api/users/change_password/', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      alert('Password changed successfully!');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      console.error('Error changing password:', error);
      alert(error.response?.data?.error || 'Failed to change password');
    }
  };

  const openLeaveTypeModal = (leaveType = null) => {
    if (leaveType) {
      setEditingLeaveType(leaveType);
      setLeaveTypeData({
        name: leaveType.name,
        code: leaveType.code,
        default_days: leaveType.default_days,
        description: leaveType.description || '',
        is_paid: leaveType.is_paid
      });
    } else {
      setEditingLeaveType(null);
      setLeaveTypeData({
        name: '',
        code: '',
        default_days: '',
        description: '',
        is_paid: true
      });
    }
    setIsModalOpen(true);
  };

  const closeLeaveTypeModal = () => {
    setIsModalOpen(false);
    setEditingLeaveType(null);
  };

  const handleLeaveTypeSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...leaveTypeData,
        default_days: parseInt(leaveTypeData.default_days)
      };

      if (editingLeaveType) {
        await api.put(`/api/hr/leave-types/${editingLeaveType.id}/`, submitData);
      } else {
        await api.post('/api/hr/leave-types/', submitData);
      }
      closeLeaveTypeModal();
      fetchLeaveTypes();
    } catch (error) {
      console.error('Error saving leave type:', error);
      alert('Failed to save leave type');
    }
  };

  const handleDeleteLeaveType = async (id) => {
    if (window.confirm('Are you sure you want to delete this leave type?')) {
      try {
        await api.delete(`/api/hr/leave-types/${id}/`);
        fetchLeaveTypes();
      } catch (error) {
        console.error('Error deleting leave type:', error);
        alert('Failed to delete leave type');
      }
    }
  };

  const leaveTypeColumns = [
    { accessor: 'code', label: 'Code' },
    { accessor: 'name', label: 'Leave Type' },
    { accessor: 'default_days', label: 'Days Allowed' },
    {
      accessor: 'is_paid',
      label: 'Paid',
      render: (type) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          type.is_paid ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
        }`}>
          {type.is_paid ? 'Yes' : 'No'}
        </span>
      )
    },
    {
      accessor: 'actions',
      label: 'Actions',
      render: (type) => (
        <div className="flex space-x-2">
          <button
            onClick={() => openLeaveTypeModal(type)}
            className="text-blue-600 hover:text-blue-900"
          >
            <PencilIcon className="h-5 w-5" />
          </button>
          <button
            onClick={() => handleDeleteLeaveType(type.id)}
            className="text-red-600 hover:text-red-900"
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        {activeTab === 'leave-types' && (
          <Button onClick={() => openLeaveTypeModal()}>
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Leave Type
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('profile')}
            className={`${
              activeTab === 'profile'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
          >
            <UserIcon className="h-5 w-5 mr-2" />
            Profile
          </button>
          <button
            onClick={() => setActiveTab('password')}
            className={`${
              activeTab === 'password'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
          >
            <KeyIcon className="h-5 w-5 mr-2" />
            Change Password
          </button>
          <button
            onClick={() => setActiveTab('leave-types')}
            className={`${
              activeTab === 'leave-types'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
          >
            <Cog6ToothIcon className="h-5 w-5 mr-2" />
            Leave Types
          </button>
          <button
            onClick={() => setActiveTab('security')}
            className={`${
              activeTab === 'security'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
          >
            <ShieldCheckIcon className="h-5 w-5 mr-2" />
            Security
          </button>
        </nav>
      </div>

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <div className="bg-white shadow rounded-lg p-6">
          <form onSubmit={handleProfileSubmit} className="space-y-6 max-w-2xl">
            <div>
              <label className="block text-sm font-medium text-gray-700">Username</label>
              <input
                type="text"
                value={user?.username || ''}
                disabled
                className="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm"
              />
              <p className="mt-1 text-sm text-gray-500">Username cannot be changed</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">First Name</label>
                <input
                  type="text"
                  value={profileData.first_name}
                  onChange={(e) => setProfileData({ ...profileData, first_name: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Last Name</label>
                <input
                  type="text"
                  value={profileData.last_name}
                  onChange={(e) => setProfileData({ ...profileData, last_name: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                value={profileData.email}
                onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <Button type="submit">Update Profile</Button>
            </div>
          </form>
        </div>
      )}

      {/* Password Tab */}
      {activeTab === 'password' && (
        <div className="bg-white shadow rounded-lg p-6">
          <form onSubmit={handlePasswordSubmit} className="space-y-6 max-w-2xl">
            <div>
              <label className="block text-sm font-medium text-gray-700">Current Password</label>
              <input
                type="password"
                required
                value={passwordData.current_password}
                onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">New Password</label>
              <input
                type="password"
                required
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
              <p className="mt-1 text-sm text-gray-500">Must be at least 8 characters long</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Confirm New Password</label>
              <input
                type="password"
                required
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <Button type="submit">Change Password</Button>
            </div>
          </form>
        </div>
      )}

      {/* Leave Types Tab */}
      {activeTab === 'leave-types' && (
        <div className="bg-white shadow rounded-lg">
          <DataTable
            columns={leaveTypeColumns}
            data={leaveTypes}
          />
        </div>
      )}

      {/* Security Tab */}
      {activeTab === 'security' && (
        <div className="space-y-6">
          {/* Mandatory 2FA Warning */}
          {requires2FASetup && (
            <div className="bg-red-50 border-l-4 border-red-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Two-Factor Authentication Required</h3>
                  <p className="mt-1 text-sm text-red-700">
                    For security purposes, all users are required to enable Two-Factor Authentication (2FA) before accessing the system.
                    Please set up 2FA below to continue.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Two-Factor Authentication */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Two-Factor Authentication</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Add an extra layer of security to your account by requiring a verification code in addition to your password.
                </p>
              </div>
              {twoFAStatus?.is_enabled && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  <svg className="mr-1.5 h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Enabled
                </span>
              )}
            </div>

            <div className="mt-6">
              {!twoFAStatus?.is_enabled && !twoFASetupData && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <ShieldCheckIcon className="h-8 w-8 text-gray-400" />
                    </div>
                    <div className="ml-4 flex-1">
                      <p className="text-sm text-gray-600">
                        Two-factor authentication is not enabled on your account. {requires2FASetup ? 'You must enable 2FA to access the system.' : 'We recommend enabling 2FA to keep your account secure.'}
                      </p>
                    </div>
                    <Button onClick={handleSetup2FA} disabled={twoFALoading}>
                      {twoFALoading ? 'Setting up...' : 'Enable 2FA'}
                    </Button>
                  </div>
                </div>
              )}

              {/* 2FA Setup Process */}
              {twoFASetupData && (
                <div className="space-y-6">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-blue-900 mb-2">Step 1: Scan QR Code</h4>
                    <p className="text-sm text-blue-700 mb-4">
                      Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                    </p>
                    <div className="flex justify-center bg-white p-4 rounded-lg max-w-xs mx-auto">
                      <img src={twoFASetupData.qr_code} alt="2FA QR Code" className="w-48 h-48" />
                    </div>
                    <p className="mt-4 text-xs text-center text-blue-600">
                      Can't scan? Enter this key manually: <code className="bg-white px-2 py-1 rounded font-mono">{twoFASetupData.secret}</code>
                    </p>
                  </div>

                  <form onSubmit={handleVerify2FASetup} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Step 2: Enter Verification Code</label>
                      <p className="text-xs text-gray-500 mb-2">Enter the 6-digit code from your authenticator app</p>
                      <input
                        type="text"
                        value={twoFACode}
                        onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="mt-1 block w-full max-w-xs rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-center text-lg tracking-widest font-mono"
                        placeholder="000000"
                        maxLength={6}
                      />
                    </div>
                    <div className="flex space-x-3">
                      <Button type="submit" disabled={twoFALoading || twoFACode.length !== 6}>
                        {twoFALoading ? 'Verifying...' : 'Verify and Enable'}
                      </Button>
                      <Button type="button" variant="secondary" onClick={() => { setTwoFASetupData(null); setTwoFACode(''); }}>
                        Cancel
                      </Button>
                    </div>
                  </form>
                </div>
              )}

              {/* 2FA Enabled Status */}
              {twoFAStatus?.is_enabled && !twoFASetupData && (
                <div className="space-y-4">
                  <div className="bg-green-50 rounded-lg p-4">
                    <p className="text-sm text-green-700">
                      ✓ Two-factor authentication is active on your account. You have {twoFAStatus.backup_codes_remaining} backup code(s) remaining.
                    </p>
                  </div>

                  <div className="flex space-x-3">
                    <Button variant="secondary" onClick={handleRegenerateCodes} disabled={twoFALoading}>
                      Regenerate Backup Codes
                    </Button>
                    <Button variant="danger" onClick={() => setShowDisableModal(true)}>
                      Disable 2FA
                    </Button>
                  </div>
                </div>
              )}

              {/* Backup Codes Display */}
              {showBackupCodes && backupCodes.length > 0 && (
                <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-yellow-900 mb-2">⚠️ Save Your Backup Codes</h4>
                  <p className="text-sm text-yellow-700 mb-4">
                    Store these codes in a safe place. Each code can only be used once if you lose access to your authenticator app.
                  </p>
                  <div className="bg-white rounded-lg p-4 font-mono text-sm grid grid-cols-2 gap-2 max-w-sm">
                    {backupCodes.map((code, index) => (
                      <div key={index} className="text-gray-700">{code}</div>
                    ))}
                  </div>
                  <div className="mt-4">
                    <Button variant="secondary" onClick={() => setShowBackupCodes(false)}>
                      I've Saved These Codes
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Session Info */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900">Session Security</h3>
            <p className="mt-1 text-sm text-gray-500">
              Your session will automatically expire after 10 minutes of inactivity. You will be warned 1 minute before automatic logout.
            </p>
          </div>
        </div>
      )}

      {/* Disable 2FA Modal */}
      <Modal
        isOpen={showDisableModal}
        onClose={() => { setShowDisableModal(false); setDisableCode(''); setDisablePassword(''); }}
        title="Disable Two-Factor Authentication"
      >
        <form onSubmit={handleDisable2FA} className="space-y-4">
          <div className="bg-red-50 rounded-lg p-4 mb-4">
            <p className="text-sm text-red-700">
              Warning: Disabling 2FA will make your account less secure. You will need to re-configure 2FA if you want to enable it again.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Authentication Code</label>
            <p className="text-xs text-gray-500 mb-2">Enter a code from your authenticator app or a backup code</p>
            <input
              type="text"
              required
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="Enter code"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Password</label>
            <input
              type="password"
              required
              value={disablePassword}
              onChange={(e) => setDisablePassword(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="Enter your password"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => { setShowDisableModal(false); setDisableCode(''); setDisablePassword(''); }}>
              Cancel
            </Button>
            <Button type="submit" variant="danger" disabled={twoFALoading}>
              {twoFALoading ? 'Disabling...' : 'Disable 2FA'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Leave Type Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeLeaveTypeModal}
        title={editingLeaveType ? 'Edit Leave Type' : 'Add Leave Type'}
      >
        <form onSubmit={handleLeaveTypeSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Code *</label>
              <input
                type="text"
                required
                value={leaveTypeData.code}
                onChange={(e) => setLeaveTypeData({ ...leaveTypeData, code: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Name *</label>
              <input
                type="text"
                required
                value={leaveTypeData.name}
                onChange={(e) => setLeaveTypeData({ ...leaveTypeData, name: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Days Allowed *</label>
            <input
              type="number"
              required
              value={leaveTypeData.default_days}
              onChange={(e) => setLeaveTypeData({ ...leaveTypeData, default_days: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Description</label>
            <textarea
              value={leaveTypeData.description}
              onChange={(e) => setLeaveTypeData({ ...leaveTypeData, description: e.target.value })}
              rows={2}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div className="flex items-center space-x-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={leaveTypeData.is_paid}
                onChange={(e) => setLeaveTypeData({ ...leaveTypeData, is_paid: e.target.checked })}
                className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Is Paid</span>
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="secondary" onClick={closeLeaveTypeModal}>
              Cancel
            </Button>
            <Button type="submit">
              {editingLeaveType ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
